/**
 * NMS Hourly Flash Deals Component
 * Time-scheduled deal slots on top of the Top Deals product carousel.
 *
 * - Exactly one slot is LIVE: the latest block whose activation time <= now.
 *   It stays LIVE until its optional end time, or - when no end time is set -
 *   until the next configured slot activates.
 * - When a slot with an end time ends before the next slot starts, nothing is
 *   live: the next slot's locked preview shows and its tab ribbon counts down
 *   "Next Deal In".
 * - Time authority is the SHOP timezone: Liquid resolves activation epochs and
 *   emits `data-server-now`; JS calibrates a monotonic clock
 *   (server epoch + performance.now() delta) so a wrong device clock can
 *   never activate the wrong deal.
 * - Upcoming tabs show blurred placeholder cards (no real product data is
 *   fetched/rendered for locked deals). The LIVE tab shows real products.
 * - The "View all" button follows the slot on screen: each slot carries its
 *   own ctaUrl/ctaText (resolved server-side with fallbacks) and the button's
 *   href/text are swapped whenever the viewed slot changes. The button is
 *   only visible while the slot on screen is LIVE - on locked (upcoming)
 *   previews and whenever no deal is live it gets `data-idle` (invisible,
 *   unclickable, but it keeps its space so nothing shifts), and it reappears
 *   on its own when that slot's deal goes live.
 * - One setInterval per section drives countdown + state transitions.
 * - Upcoming collection is prefetched when its activation is <= 2 minutes
 *   away, so the transition is instant. Fetched item nodes are cached.
 * - Product markup comes from Liquid via the Section Rendering API
 *   ({collection_url}?section_id={items_section}); the scroll-carousel
 *   element is never replaced - only its children swap.
 *
 * Follows NMS component instance architecture (extends NMSBaseClass).
 * Scoped per section instance - multiple sections don't interfere.
 */

const TICK_MS = 1000;
const PREFETCH_LEAD_MS = 2 * 60 * 1000; // start preloading the next deal

const LOCK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4.75" y="10.75" width="14.5" height="9.5" rx="2"/><path d="M8.25 10.75V8a3.75 3.75 0 1 1 7.5 0v2.75"/><circle cx="12" cy="15.25" r="1" fill="currentColor" stroke="none"/></svg>';

const LIVE_DOT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><rect class="nms-live-indicator__ring" x="2" y="2.5" width="12" height="12" rx="6" fill="black" fill-opacity="0.3"/><rect x="5" y="5.5" width="6" height="6" rx="3" fill="black" fill-opacity="0.8"/></svg>';

class NMSHourlyFlashDeals extends window.NMSBaseClass {
  constructor(element) {
    super();

    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;
    this.progressId = element.dataset.progressId || this.sectionId;
    this.itemsSection = element.dataset.itemsSection || 'nms-top-deals-items';

    this.slots = []; // [{ index, label, time, unlock, ts (seconds), duration (seconds, 0 = none), handle, url, ctaUrl, ctaText }]
    this.carousel = null;
    this.timer = null;

    // Monotonic server clock: render-time server epoch + elapsed perf time.
    // Device clock is never consulted for schedule decisions.
    this.epochBaseMs = 0;
    this.perfBaseMs = 0;
    this.tzOffsetMin = 0; // shop timezone offset (minutes east of UTC)

    this.liveIndex = -1; // slot currently LIVE per the schedule
    this.viewingIndex = -1; // slot whose content the carousel shows
    this.viewingLocked = false;

    this.collectionCache = new Map(); // handle -> Array<Element>
    this.pendingRequests = new Map(); // handle -> { promise, controller }
    this.lockedItems = new Map(); // slot index -> Array<Element> (placeholders)

    // Live card height -> --nms-flash-card-height on the section: locked
    // preview cards match it, so switching tabs doesn't jump the page. The
    // last measured value persists while locked previews are on screen.
    this.cardHeightObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(entries => {
            const entry = entries[0];
            const box = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
            const height = Math.round(box || 0);
            if (height > 0) {
              this.element.style.setProperty('--nms-flash-card-height', `${height}px`);
            }
          })
        : null;

    window.neemans = window.neemans || {};
    window.neemans.hourlyFlashDeals = window.neemans.hourlyFlashDeals || {};
    window.neemans.hourlyFlashDeals[this.sectionId] = this;
  }

  init() {
    super.init(); // binds [ref] + [data-nms-click]

    this.carousel = this.element.querySelector('scroll-carousel');

    const scheduleNode = this.element.querySelector('[data-schedule]');
    try {
      const data = JSON.parse(scheduleNode?.textContent || '{}');
      this.slots = Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('NMS Hourly Flash Deals: invalid schedule JSON', error);
      this.slots = [];
    }

    // Calibrate the monotonic clock against the render-time server epoch
    const serverNowSec = parseInt(this.element.dataset.serverNow || '0', 10);
    this.epochBaseMs = serverNowSec * 1000;
    this.perfBaseMs = performance.now();

    // Shop timezone offset (e.g. "+0530") — lets JS map slot times onto the
    // CURRENT shop day, so a page open across midnight still schedules right.
    this.tzOffsetMin = this.#parseTzOffset(this.element.dataset.tzOffset);

    // Resolve each slot's time-of-day onto today's shop date
    this.#resolveSlots(this.#now());

    // Initial state is computed directly - #syncState's activation hook only
    // fires on TRANSITIONS after init (otherwise the SSR'd live collection
    // would be fetched again before it lands in the cache).
    const now = this.#now();
    this.liveIndex = this.#liveIndexAt(now);

    const ssrItems = Array.from(this.carousel?.querySelectorAll('[data-deal-item]') || []);
    const ssrIsLocked = ssrItems.length > 0 && ssrItems[0].hasAttribute('data-locked-item');

    // Cache SSR products under the live slot's collection (locked SSR items
    // are placeholders, never product data)
    if (!ssrIsLocked && ssrItems.length > 0 && this.liveIndex > -1 && this.slots[this.liveIndex]?.handle) {
      this.collectionCache.set(this.slots[this.liveIndex].handle, ssrItems);
    }

    this.viewingLocked = ssrIsLocked;
    this.viewingIndex = ssrIsLocked ? this.#nextIndexAt(now) : this.liveIndex;

    // Stale render: SSR'd real products but the deal is no longer live
    // (e.g. a date-pinned slot's day ended, or the slot's end time passed,
    // since render) — swap to the next slot's locked preview so nothing real
    // stays on screen.
    if (!this.viewingLocked && this.liveIndex === -1) {
      const nextIndex = this.#nextIndexAt(now);
      if (nextIndex !== -1) {
        this.viewingIndex = nextIndex;
        this.viewingLocked = true;
        this.#showLockedPreview(this.slots[nextIndex]);
      }
    }

    this.#renderTabs();
    this.#watchCardHeight();
    this.#updateViewAll();
    this.#updateCouponStrip();
    this.#updateCountdown(now);
    this.#checkAvailability(now);
    this.timer = window.setInterval(() => this.#tick(), TICK_MS);
  }

  destroy() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.cardHeightObserver?.disconnect();
    for (const entry of this.pendingRequests.values()) {
      entry.controller.abort();
    }
    this.pendingRequests.clear();

    super.destroy();

    if (window.neemans.hourlyFlashDeals?.[this.sectionId]) {
      delete window.neemans.hourlyFlashDeals[this.sectionId];
    }
  }

  /* ================================================================
     Clock
     ================================================================ */

  /** Current server/shop time in ms - monotonic, device-clock independent */
  #now() {
    return this.epochBaseMs + (performance.now() - this.perfBaseMs);
  }

  /**
   * Parse "+0530"-style shop timezone offset into minutes.
   * @param {string} offset
   * @returns {number} minutes east of UTC
   * @private
   */
  #parseTzOffset(offset) {
    const match = /^([+-])(\d{2})(\d{2})$/.exec(offset || '');
    if (!match) return 0;
    const minutes = parseInt(match[2], 10) * 60 + parseInt(match[3], 10);
    return match[1] === '-' ? -minutes : minutes;
  }

  /**
   * Resolve each slot's configured time-of-day onto TODAY's shop date.
   * Slots are daily-recurring: each activation is today at that shop-local
   * time. Correct even if the HTML was rendered before midnight. Slots with
   * an end time also get `endAt` (activation + duration, in seconds).
   * @param {number} now - Server time in ms
   * @private
   */
  #resolveSlots(now) {
    const tzSec = this.tzOffsetMin * 60;
    const nowSec = Math.floor(now / 1000);
    // Start of the current shop day, as a unix epoch
    const shopDayStart = Math.floor((nowSec + tzSec) / 86400) * 86400 - tzSec;

    for (const slot of this.slots) {
      if (slot.recurring) {
        // Daily slot: re-anchor its time-of-day onto the current shop day
        const tod = (((slot.ts + tzSec) % 86400) + 86400) % 86400;
        slot.dayTs = shopDayStart + tod;
        slot.dayEndTs = null;
      } else {
        // Date-pinned slot: fixed absolute activation; expires at the end
        // of its own shop-calendar day
        slot.dayTs = slot.ts;
        slot.dayEndTs = Math.floor((slot.ts + tzSec) / 86400) * 86400 - tzSec + 86400;
      }

      // Optional end time: the deal stops being live at this moment
      slot.endAt = slot.duration > 0 ? slot.dayTs + slot.duration : null;
    }
  }

  /* ================================================================
     Schedule state machine
     ================================================================ */

  #tick() {
    const now = this.#now();
    this.#resolveSlots(now); // re-anchors recurring slots if the shop day rolled
    this.#syncState(now);
    this.#updateCountdown(now);
    this.#checkAvailability(now);
    this.#maybePrefetch(now);
  }

  /**
   * Recompute live/next slot from the clock; on activation promote the next
   * deal - previous deal expires (tab removed), new deal loads its products.
   * @param {number} now - Server time in ms
   * @private
   */
  #syncState(now) {
    const previousLive = this.liveIndex;
    this.liveIndex = this.#liveIndexAt(now);

    if (this.liveIndex !== previousLive) {
      this.#onActivation(previousLive);
    }
  }

  /** Last slot whose activation time has passed; -1 before the first deal.
   *  Date-pinned slots stop counting once their shop day ends. A slot whose
   *  end time has passed is no longer live (nothing falls back to an earlier
   *  slot - the later slot superseded it). */
  #liveIndexAt(now) {
    let live = -1;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const activated = slot.dayTs * 1000 <= now;
      const stillValid = slot.recurring || now < slot.dayEndTs * 1000;
      if (activated && stillValid) live = i;
    }

    if (live > -1) {
      const endAt = this.slots[live].endAt;
      if (endAt && now >= endAt * 1000) live = -1;
    }
    return live;
  }

  /** First slot that has not activated yet; -1 when none remain */
  #nextIndexAt(now) {
    for (let i = 0; i < this.slots.length; i++) {
      if (this.slots[i].dayTs * 1000 > now) return i;
    }
    return -1;
  }

  /**
   * Live state changed (a deal activated, or the live deal ended). Refresh
   * tabs and swap the carousel (from cache when prefetched, else fetch).
   * @param {number} previousLive - Slot index that was live before
   * @private
   */
  #onActivation(previousLive) {
    this.liveIndex = this.#liveIndexAt(this.#now());

    if (this.liveIndex === -1) {
      // The live deal ended with no deal live right now (end time reached, or
      // a date-pinned deal hit the end of its day) - fall back to the next
      // slot's locked preview so the section still communicates what's coming.
      const nextIndex = this.#nextIndexAt(this.#now());
      if (nextIndex !== -1) {
        this.viewingIndex = nextIndex;
        this.viewingLocked = true;
        this.#showLockedPreview(this.slots[nextIndex]);
      }
    } else {
      // A new deal went live - swap the carousel to its products (served from
      // the prefetch cache when available) and reset the carousel position.
      this.viewingIndex = this.liveIndex;
      this.viewingLocked = false;
      this.#showSlotProducts(this.slots[this.liveIndex]);

      this.dispatchNMSEvent('flash-deal-activated', {
        sectionId: this.sectionId,
        collection: this.slots[this.liveIndex].handle,
      });
    }

    this.#renderTabs();
    this.#updateViewAll();
    this.#updateCouponStrip();
  }

  /* ================================================================
     Countdown
     ================================================================ */

  /**
   * Update the countdown ribbon: it hangs off the tab that owns the timer —
   * the LIVE tab while a deal is running ("Deal Ends In", counting to its
   * end time else the next activation), otherwise the next slot's tab
   * ("Next Deal In"). Hidden when there is nothing to count down to.
   * @param {number} now - Server time in ms
   * @private
   */
  #updateCountdown(now) {
    const tabs = this.refs.dealTabs || [];

    // The countdown boundary is the next activation — or, for a live deal,
    // the earlier of its end time and (for a date-pinned deal) the end of
    // its shop day.
    let targetMs = null;
    const nextIndex = this.#nextIndexAt(now);
    if (nextIndex !== -1) {
      targetMs = this.slots[nextIndex].dayTs * 1000;
    }

    const live = this.liveIndex > -1 ? this.slots[this.liveIndex] : null;
    if (live && !live.recurring && live.dayEndTs) {
      const dayEndMs = live.dayEndTs * 1000;
      if (targetMs === null || dayEndMs < targetMs) targetMs = dayEndMs;
    }
    if (live && live.endAt) {
      const endMs = live.endAt * 1000;
      if (targetMs === null || endMs < targetMs) targetMs = endMs;
    }

    // Ribbon owner: the live slot's tab while a deal runs, else the next slot's
    const ownerIndex = this.liveIndex > -1 ? this.liveIndex : nextIndex;
    const label =
      this.liveIndex > -1 ? this.element.dataset.endsLabel : this.element.dataset.nextLabel;

    let timeText = '';
    if (targetMs !== null) {
      const totalSeconds = Math.max(0, Math.floor((targetMs - now) / 1000));
      timeText = [
        Math.floor(totalSeconds / 3600),
        Math.floor((totalSeconds % 3600) / 60),
        totalSeconds % 60,
      ]
        .map(unit => String(unit).padStart(2, '0'))
        .join(':');
    }

    for (const tab of tabs) {
      const ribbon = tab.querySelector('.nms-hourly-flash-deals__tab-ribbon');
      if (!ribbon) continue;

      const index = parseInt(tab.dataset.slotIndex || '-1', 10);
      const show = targetMs !== null && index === ownerIndex;
      ribbon.hidden = !show;
      // The countdown takes turns with the tab label inside the pill
      tab.classList.toggle('nms-hourly-flash-deals__tab--rotating', show);
      if (!show) continue;

      const labelEl = ribbon.querySelector('.nms-hourly-flash-deals__tab-ribbon-label');
      const timeEl = ribbon.querySelector('.nms-hourly-flash-deals__tab-ribbon-time');
      if (labelEl && label) labelEl.textContent = label;
      if (timeEl) timeEl.textContent = timeText;
    }
  }

  /**
   * Hide the section entirely when no deal is live AND none is upcoming
   * (e.g. the last slot has ended, or only date-pinned slots and they've
   * all passed).
   * @param {number} now - Server time in ms
   * @private
   */
  #checkAvailability(now) {
    const available = this.liveIndex > -1 || this.#nextIndexAt(now) > -1;
    this.element.hidden = !available;
  }

  /* ================================================================
     Tabs
     ================================================================ */

  // Public event handler - auto-bound via data-nms-click
  handleTabClick(event) {
    event.preventDefault();
    const tab = event.currentTarget;
    const index = parseInt(tab.dataset.slotIndex || '-1', 10);
    if (index < 0 || index >= this.slots.length) return;
    this.selectSlot(index);
  }

  /**
   * Select a deal tab. LIVE -> real products; upcoming -> locked preview
   * (placeholder cards only - never fetches that deal's real products).
   * @param {number} index - Slot index in this.slots
   */
  selectSlot(index) {
    const slot = this.slots[index];
    if (!slot) return;

    if (index === this.liveIndex) {
      if (this.viewingIndex === index && !this.viewingLocked) return;
      this.viewingIndex = index;
      this.viewingLocked = false;
      this.#showSlotProducts(slot);
      this.#renderTabs();
      this.#updateViewAll();
    this.#updateCouponStrip();
    } else if (slot.dayTs * 1000 > this.#now()) {
      if (this.viewingIndex === index && this.viewingLocked) return;
      this.viewingIndex = index;
      this.viewingLocked = true;
      this.#showLockedPreview(slot);
      this.#renderTabs();
      this.#updateViewAll();
    this.#updateCouponStrip();
    }
  }

  /**
   * Sync the "View all" button with the slot on screen: href/text come from
   * that slot (resolved server-side: slot -> section default -> collection
   * URL). It is visible ONLY while that slot's deal is live; otherwise it is
   * marked data-idle (CSS: visibility hidden + no pointer events), which keeps
   * its space so the layout never shifts between tabs.
   * @private
   */
  #updateViewAll() {
    const slot = this.slots[this.viewingIndex];
    const wrap = this.refs.viewAllWrap;
    const link = this.refs.viewAll;

    if (wrap) {
      const idle = !slot || this.viewingLocked || this.viewingIndex !== this.liveIndex;
      wrap.toggleAttribute('data-idle', idle);
    }

    if (!slot || !link || !slot.ctaUrl) return;

    link.setAttribute('href', slot.ctaUrl);
    if (this.refs.viewAllText && slot.ctaText) {
      this.refs.viewAllText.textContent = slot.ctaText;
    }
  }

  /**
   * Sync the offer/coupon strip with the slot on screen — every slot carries
   * its own discount (resolved server-side: slot fields -> section defaults).
   * `auto` renders the discount as a plain text chip; `coupon` renders the
   * copyable "Use Code:" pill. Content is rebuilt from inert templates.
   * @private
   */
  #updateCouponStrip() {
    const strip = this.element.querySelector('[data-flash-coupon]');
    if (!strip) return;

    const slot = this.slots[this.viewingIndex];
    const title = slot?.discountTitle || '';
    const text = slot?.discountText || '';
    const isAuto = slot?.discountType === 'auto';

    if (!title && !text) {
      strip.hidden = true;
      return;
    }
    strip.hidden = false;
    strip.classList.toggle('nms-flash-coupon--auto', isAuto);

    const template = this.element.querySelector(
      isAuto ? 'template[data-coupon-auto]' : 'template[data-coupon-coupon]'
    );
    if (!template) return;

    strip.replaceChildren(template.content.cloneNode(true));

    const titleEl = strip.querySelector('[data-discount-title]');
    if (title) {
      titleEl.textContent = title;
    } else {
      titleEl?.closest('[data-coupon-offer]')?.remove();
    }

    const pill = strip.querySelector('[data-coupon-pill]');
    if (!text) {
      pill?.remove();
      strip.querySelector('[data-flash-coupon-status]')?.remove();
      return;
    }

    const codeEl = strip.querySelector('[data-discount-text]');
    if (codeEl) codeEl.textContent = isAuto ? text : text.toUpperCase();
    if (!isAuto && pill) {
      const code = text.toUpperCase();
      pill.dataset.flashCouponCopy = code;
      pill.setAttribute('aria-label', `Copy coupon code ${code}`);
    }
  }

  /** Paint tab visuals per state: live / upcoming / expired(hidden) */
  #renderTabs() {
    const now = this.#now();
    const tabs = this.refs.dealTabs || [];

    for (const tab of tabs) {
      const index = parseInt(tab.dataset.slotIndex || '-1', 10);
      const slot = this.slots[index];
      if (!slot) continue;

      const isLive = index === this.liveIndex;
      const isExpired = !isLive && slot.dayTs * 1000 <= now;
      // The viewed tab owns the carousel content — a locked deal's tab stays
      // marked while its locked preview is on screen
      const isViewing = index === this.viewingIndex;

      tab.hidden = isExpired;
      tab.classList.toggle('nms-hourly-flash-deals__tab--live', isLive);
      tab.classList.toggle('nms-hourly-flash-deals__tab--upcoming', !isLive);
      tab.classList.toggle('nms-hourly-flash-deals__tab--viewing', isViewing);
      tab.setAttribute('aria-selected', isViewing ? 'true' : 'false');

      const text = tab.querySelector('.nms-hourly-flash-deals__tab-text');
      const icon = tab.querySelector('.nms-hourly-flash-deals__tab-lock');
      const dot = tab.querySelector('.nms-hourly-flash-deals__live-dot');
      // Dot/lock live in the label face so they flip together with the text
      const face = tab.querySelector('.nms-hourly-flash-deals__tab-face') || tab;

      if (isLive) {
        if (icon) icon.remove();
        if (!dot) {
          const newDot = document.createElement('span');
          newDot.className = 'nms-hourly-flash-deals__live-dot nms-live-indicator';
          newDot.setAttribute('aria-hidden', 'true');
          newDot.innerHTML = LIVE_DOT_SVG;
          face.prepend(newDot);
        }
        if (text) text.textContent = `LIVE · ${slot.label}`;
      } else {
        if (dot) dot.remove();
        if (!icon) {
          const newIcon = document.createElement('span');
          newIcon.className = 'nms-hourly-flash-deals__tab-lock';
          newIcon.setAttribute('aria-hidden', 'true');
          newIcon.innerHTML = LOCK_SVG;
          face.prepend(newIcon);
        }
        // Upcoming pill shows the time only (no date), e.g. "6 PM · Prime Time Drop"
        if (text) text.textContent = `${slot.time} · ${slot.label}`;
      }
    }
  }

  /* ================================================================
     Locked preview
     ================================================================ */

  /**
   * Swap the carousel to blurred placeholder cards for an upcoming deal.
   * Placeholders are built once per slot and cached - real products for an
   * upcoming deal are never fetched.
   * @param {object} slot
   * @private
   */
  #showLockedPreview(slot) {
    if (!this.carousel) return;

    let items = this.lockedItems.get(slot.index);
    if (!items) {
      const template = this.element.querySelector('template[data-locked-template]');
      if (!template) return;
      items = [];
      for (let i = 0; i < 4; i++) {
        const node = template.content.firstElementChild.cloneNode(true);
        const timeNode = node.querySelector('[data-unlock-time]');
        if (timeNode) timeNode.textContent = slot.unlock;
        items.push(node);
      }
      this.lockedItems.set(slot.index, items);
    }

    this.carousel.replaceChildren(...items.map(node => node.cloneNode(true)));
    this.#toggleEmptyState(false);
    this.#watchCardHeight();
    this.#refreshCarousel();
  }

  /* ================================================================
     Product loading (Section Rendering API - same as Top Deals)
     ================================================================ */

  /**
   * Show a LIVE deal's products: cache hit -> instant swap; otherwise fetch
   * the collection's rendered cards via the Section Rendering API.
   * @param {object} slot
   * @private
   */
  async #showSlotProducts(slot) {
    if (!this.carousel || !slot?.url) return;

    const cached = this.collectionCache.get(slot.handle);
    if (cached) {
      this.#applyItems(cached);
      return;
    }

    this.#setLoading(true);
    try {
      const items = await this.#requestCollection(slot);
      // Race guard: only apply if this slot is still being viewed live
      if (this.viewingIndex !== slot.index || this.viewingLocked) return;
      this.#applyItems(items);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(`NMS Hourly Flash Deals: failed to load "${slot.handle}":`, error);
      }
    } finally {
      if (this.viewingIndex === slot.index && !this.viewingLocked) {
        this.#setLoading(false);
      }
    }
  }

  /**
   * Fetch rendered deal items. Dedupes in-flight requests and caches nodes.
   * @param {object} slot
   * @returns {Promise<Element[]>}
   * @private
   */
  async #requestCollection(slot) {
    if (this.collectionCache.has(slot.handle)) {
      return this.collectionCache.get(slot.handle);
    }

    const existing = this.pendingRequests.get(slot.handle);
    if (existing) {
      return existing.promise;
    }

    const controller = new AbortController();
    const promise = (async () => {
      const response = await fetch(`${slot.url}?section_id=${encodeURIComponent(this.itemsSection)}`, {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('[data-deal-item]'));

      this.collectionCache.set(slot.handle, items);
      return items;
    })();

    const entry = { promise, controller };
    entry.promise.finally(() => {
      if (this.pendingRequests.get(slot.handle) === entry) {
        this.pendingRequests.delete(slot.handle);
      }
    });
    this.pendingRequests.set(slot.handle, entry);

    return promise;
  }

  /**
   * Preload the next deal's collection shortly before it activates so the
   * transition is instant. Silent failure is fine - activation falls back
   * to a normal fetch.
   * @param {number} now - Server time in ms
   * @private
   */
  #maybePrefetch(now) {
    const nextIndex = this.#nextIndexAt(now);
    if (nextIndex === -1) return;

    const remaining = this.slots[nextIndex].dayTs * 1000 - now;
    if (remaining > PREFETCH_LEAD_MS) return;

    const slot = this.slots[nextIndex];
    if (!slot.url || this.collectionCache.has(slot.handle) || this.pendingRequests.has(slot.handle)) {
      return;
    }

    this.#requestCollection(slot).catch(() => {});
  }

  /**
   * Swap carousel items. Children are replaced - the scroll-carousel element
   * persists so controls and scroll-progress keep working.
   * @param {Element[]} items
   * @private
   */
  #applyItems(items) {
    if (!this.carousel) return;

    this.carousel.replaceChildren(...items);
    this.#toggleEmptyState(items.length === 0);

    if (typeof window.initNMSProductCard === 'function') {
      window.initNMSProductCard();
    }

    this.#watchCardHeight();
    this.#refreshCarousel();
  }

  /**
   * Track the live deal card's height so locked preview cards can match it
   * (--nms-flash-card-height on the section). A real card in the carousel
   * gets observed (ResizeObserver fires with its current size immediately,
   * then on every resize — lazy images, fonts, breakpoint changes); locked
   * previews contain no real card, so observing stops and the last measured
   * height persists.
   * @private
   */
  #watchCardHeight() {
    if (!this.cardHeightObserver) return;
    this.cardHeightObserver.disconnect();
    const card = this.carousel?.querySelector('.nms-product-card');
    if (card) this.cardHeightObserver.observe(card);
  }

  /**
   * Reset scroll position + refresh carousel-bound UI for the new item set.
   * @private
   */
  #refreshCarousel() {
    if (!this.carousel) return;

    window.requestAnimationFrame(() => {
      const isScrollable = this.carousel.scrollWidth - this.carousel.clientWidth > 1;
      this.carousel.classList.toggle('is-scrollable', isScrollable);

      if (typeof this.carousel.select === 'function') {
        this.carousel.select(0, { instant: true });
      }
      this.carousel.scrollTo({ left: 0, behavior: 'auto' });

      const prevButton = this.element.querySelector('carousel-prev-button')?.firstElementChild;
      const nextButton = this.element.querySelector('carousel-next-button')?.firstElementChild;
      if (prevButton) prevButton.disabled = true;
      if (nextButton) nextButton.disabled = !isScrollable;

      this.carousel.dispatchEvent(new Event('scroll', { bubbles: true }));

      const progress = window.neemans?.carouselScrollProgress?.[this.progressId];
      if (progress && typeof progress.updateProgress === 'function') {
        progress.updateProgress();
      }
    });
  }

  #setLoading(isLoading) {
    if (this.refs.loadingState) {
      this.refs.loadingState.hidden = !isLoading;
    }
    if (this.carousel) {
      this.carousel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.carousel.classList.toggle('nms-top-deals__track--loading', isLoading);
    }
  }

  #toggleEmptyState(isEmpty) {
    if (this.refs.emptyState) {
      this.refs.emptyState.hidden = !isEmpty;
    }
    if (this.carousel) {
      this.carousel.classList.toggle('nms-top-deals__track--empty', isEmpty);
    }
  }
}

// Standard component initialization pattern
function initNMSHourlyFlashDeals() {
  if (!window.NMSBaseClass) {
    console.error('NMS Hourly Flash Deals: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-hourly-flash-deals]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      try {
        const instance = new NMSHourlyFlashDeals(element);
        instance.init();
        element.setAttribute('data-initialized', 'true');
      } catch (error) {
        console.error('Failed to initialize NMS Hourly Flash Deals:', error, element);
      }
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSHourlyFlashDeals);
} else {
  initNMSHourlyFlashDeals();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSHourlyFlashDeals);

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', initNMSHourlyFlashDeals);
}

// Export for debugging and manual initialization
window.initNMSHourlyFlashDeals = initNMSHourlyFlashDeals;
window.getNMSHourlyFlashDeals = function (sectionId) {
  if (sectionId) {
    return window.neemans?.hourlyFlashDeals?.[sectionId] || null;
  }
  return window.neemans?.hourlyFlashDeals || {};
};
